import React, { useState } from 'react';
import './App.css';
// import ReactMarkdown from 'react-markdown';
import { useEffect, useRef } from "react";

// Unset = same origin: Vercel uses /api/*, local dev uses proxy to server. Set = external API base URL.
const API_BASE = process.env.REACT_APP_API_BASE || '';
const api = (path) => (API_BASE ? `${API_BASE}${path}` : `/api${path}`);
const chatApi = api('/chat');
const chatApiHistory = (uid) => `${api('/chat-history')}?uid=${encodeURIComponent(uid)}`;
const clearChatApi = api('/clear-chat');

/** Parse API response safely; handles 504/timeouts and non-JSON body so we don't crash. */
async function parseApiResponse(response) {
  const text = await response.text();
  let data = null;
  try {
    if (text && text.trim()) data = JSON.parse(text);
  } catch {
    /* non-JSON (e.g. HTML error page) */
  }
  const ok = response.ok && data !== null;
  const error = !ok
    ? (data?.error || data?.reply || (response.status === 504 ? "Request timed out. Please try again." : text?.slice(0, 100) || `Error ${response.status}`))
    : null;
  return { ok, data, error };
}

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);


  // Onboarding state
  const [bonusType, setBonusType] = useState(localStorage.getItem('bonusType') || '');
  const [wager, setWager] = useState(localStorage.getItem('wager') || '');
  const [preference, setPreference] = useState(localStorage.getItem('preference') || '');
  const [step, setStep] = useState(1);
  const [onboarded, setOnboarded] = useState(localStorage.getItem('onboarded') === 'true');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const uid = localStorage.getItem("chat_user_id");
    if (!uid) return;
  
    const loadHistory = async () => {
      const uid = localStorage.getItem("chat_user_id");
      if (!uid) return;
      
      try {
        const res = await fetch(chatApiHistory(uid), {
          headers: {
            "x-api-key": process.env.REACT_APP_API_KEY,
          },
        });
        const { ok, data } = await parseApiResponse(res);
        if (ok && data) {
          if (data.messages) {
            const formatted = data.messages.map((msg) => ({
              sender: msg.role === "user" ? "user" : "bot",
              text: msg.content,
            }));
            scrollToBottom();
            setMessages(formatted);
          } else if (data.reply) {
            scrollToBottom();
            setMessages([{ sender: "bot", text: data.reply }]);
          }
        }
      } catch (err) {
        console.error("Failed to load chat history", err);
      }
    };
       
  
    loadHistory();
  }, []);
  
  
  const params = new URLSearchParams(window.location.search);
  const encodedUid = params.get("uid");
  const countrySlug = params.get("country_name") || "";
  const countryISO = params.get("country_iso") || "";
  const state = params.get("state") || "";
  let countryNationality = countrySlug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()); 
  const showQuickReplies = onboarded && encodedUid;



  const getUserId = () => {
    let uid = localStorage.getItem('chat_user_id');
    if (encodedUid) {
      const userID = atob(encodedUid); // decode from base64
      localStorage.setItem("chat_user_id", userID);
    }else{
      uid = 'guest_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('chat_user_id', uid);
    }
    return uid;
  };

  const uid = getUserId();

  // Handle onboarding steps
  const handleOnboarding = (choice) => {
    if (step === 1) {
      setBonusType(choice);
      localStorage.setItem('bonusType', choice);
      setStep(2);
    } else if (step === 2) {
      setWager(choice);
      localStorage.setItem('wager', choice);
      setStep(3);
    } else if (step === 3) {
      setPreference(choice);
      localStorage.setItem('preference', choice);
      localStorage.setItem('onboarded', 'true');
      setOnboarded(true);
      sendInitialPreferenceMessage(choice);
    }
  };
  

  const sendInitialPreferenceMessage = async (pref) => {
    const onboardingPrompt = `I'm looking for a ${bonusType} bonus with a wagering limit of ${wager}, and I prefer ${pref} bonuses.`;

    setLoading(true);
    const userMessage = { sender: 'user', text: onboardingPrompt };
    scrollToBottom();
    setMessages([userMessage]);

    const response = await fetch(chatApi, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({
        uid,
        message: onboardingPrompt,
        countryNationality: countryNationality,
        stateName: state,
        countryISO: countryISO,
        context: {
          bonusType,
          wager,
          preference
        }
      })
    });

    const { ok, data, error } = await parseApiResponse(response);
    const botMessage = { sender: 'bot', text: ok && data?.reply ? data.reply : (error || "Something went wrong. Please try again.") };
    setMessages(prev => [...prev, botMessage]);
    scrollToBottom();
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { sender: 'user', text: input };
    scrollToBottom();
    setMessages([...messages, userMessage]);
    setInput('');
    setLoading(true);

    const response = await fetch(chatApi, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({
        uid,
        message: input,
        countryNationality: countryNationality,
        stateName: state,
        countryISO: countryISO,
        context: {
          bonusType,
          wager,
          preference
        }
      })
    });

    const { ok, data, error } = await parseApiResponse(response);
    const botMessage = { sender: 'bot', text: ok && data?.reply ? data.reply : (error || "Something went wrong. Please try again.") };
    setMessages(prev => [...prev, botMessage]);
    scrollToBottom();
    setLoading(false);
  };

  // const resetPreferences = () => {
  //   localStorage.removeItem('bonusType');
  //   localStorage.removeItem('wager');
  //   localStorage.removeItem('preference');
  //   localStorage.removeItem('onboarded');
  //   setBonusType('');
  //   setWager('');
  //   setPreference('');
  //   setStep(1);
  //   setOnboarded(false);
  //   setMessages([]);
  // };

  const sendQuickMessage = async (message) => {
    const userMessage = { sender: 'user', text: message };
    setMessages([...messages, userMessage]);
    scrollToBottom();
    setLoading(true);
  
    const response = await fetch(chatApi, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({
        uid,
        message: message,
        countryNationality: countryNationality,
        stateName: state,
        countryISO: countryISO,
        context: {
          bonusType,
          wager,
          preference
        }
      })
    });

    const { ok, data, error } = await parseApiResponse(response);
    const botMessage = { sender: 'bot', text: ok && data?.reply ? data.reply : (error || "Something went wrong. Please try again.") };
    setMessages(prev => [...prev, botMessage]);
    scrollToBottom();
    setLoading(false);
  };

  const clearChatHistory = async () => {
    const uid = localStorage.getItem("chat_user_id");
    if (!uid) return;
  
    if (!window.confirm("Are you sure you want to clear your chat history?")) return;
  
    try {
      const res = await fetch(clearChatApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ uid }),
      });
      const { ok, data } = await parseApiResponse(res);
      if (ok && data?.success) {
        setMessages([]);
        alert("Chat history cleared ✅");
      } else {
        alert(data?.error || "Failed to clear chat history.");
      }
    } catch (err) {
      console.error("Clear chat error:", err);
      alert("Something went wrong clearing chat.");
    }
  };
  

  // Onboarding UI
  if (!onboarded) {
    return (
      <div className="chat-container">
        <div className='chat-container__header'>
          <h2>AI Bonus Agent</h2>
        </div>
        <div className="chat-box">
          {step === 1 && (
            <>
              <p>👉 What type of bonus are you looking for?</p>
              <button onClick={() => handleOnboarding('Free Spins')}>Free Spins</button>
              <button onClick={() => handleOnboarding('No Deposit')}>No Deposit</button>
              <button onClick={() => handleOnboarding('Deposit Match')}>Deposit Match</button>
            </>
          )}
          {step === 2 && (
            <>
              <p>🎯 What’s the maximum wagering requirement?</p>
              <button onClick={() => handleOnboarding('40x')}>40x</button>
              <button onClick={() => handleOnboarding('50x')}>50x</button>
              <button onClick={() => handleOnboarding('Doesn’t matter')}>Doesn’t matter</button>
            </>
          )}
          {step === 3 && (
            <>
              <p>✨ Do you prefer exclusive or high-value offers?</p>
              <button onClick={() => handleOnboarding('exclusive')}>Exclusive</button>
              <button onClick={() => handleOnboarding('high value')}>High Value</button>
            </>
          )}
        </div>
        <div className="input-area">
          <input
            type="text"
            disabled
            placeholder="Ask me something..."
            className="chat-input"
            value={""}
          />
          <button className="chat-send-button">        
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            style={{ marginRight: "6px" }}
          >
            <path
              d="M14.619,23a1.05,1.05,0,0,1-.938-.579L10.572,16.2a1.052,1.052,0,0,1,.075-1.066l5.02-6.8-6.8,5.018a1.05,1.05,0,0,1-1.065.075L1.579,10.318A1.048,1.048,0,0,1,1.685,8.4L21.59,1.064a1.047,1.047,0,0,1,1.345,1.345L15.6,22.315a1.046,1.046,0,0,1-.925.683Z"
              fill="#FFFFFF"
            />
          </svg>
          </button>
        </div>
      </div>
    );
  }

  // Chat UI
  return (
    <div className="chat-container">
      <div className='chat-container__header'>
        <h2>AI Bonus Agent</h2>
        <button className="clear-btn" onClick={clearChatHistory}>Clear Chat</button>
      </div>

      <div className="chat-box">
        {messages.map((msg, i) => (
          <div key={i} className={`message ${msg.sender}`}>
            {/* <ReactMarkdown>{msg.text}</ReactMarkdown> */}
            {msg.sender === "bot" ? (
            <div
              className="message-bubble"
              dangerouslySetInnerHTML={{ __html: msg.text }}
            ></div>
          ) : (
            <div className="message-bubble">{msg.text}</div>
          )}
          </div>
        ))}
        {loading && <div className="message bot"><span className='loader'></span></div>}
        <div ref={messagesEndRef}></div>
        {showQuickReplies && (
          <div className="quick-replies">
            <p>Try one of these:</p>
            <button onClick={() => sendQuickMessage("Show me the best no deposit bonuses")}>No Deposit Bonuses I can grab</button>
            <button onClick={() => sendQuickMessage("I want free spins under 40x wagering")}>Free Spins under 40x wageging</button>
            <button onClick={() => sendQuickMessage("What bonuses don’t require a deposit?")}>Bonus With No Deposit Required</button>
          </div>
        )}
      </div>

      <div className="input-area">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder="Ask me something..."
          className="chat-input"
        />
        <button type="button" onClick={sendMessage} className="chat-send-button">        
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          style={{ marginRight: "6px" }}
        >
          <path
            d="M14.619,23a1.05,1.05,0,0,1-.938-.579L10.572,16.2a1.052,1.052,0,0,1,.075-1.066l5.02-6.8-6.8,5.018a1.05,1.05,0,0,1-1.065.075L1.579,10.318A1.048,1.048,0,0,1,1.685,8.4L21.59,1.064a1.047,1.047,0,0,1,1.345,1.345L15.6,22.315a1.046,1.046,0,0,1-.925.683Z"
            fill="#FFFFFF"
          />
        </svg>
        </button>
      </div>
    </div>
  );
}

export default App;
